/**
 * Onboarding · Step 7 — Multi-select the brief + all three images, then
 * send the pre-filled deck prompt.
 *
 * Two-phase:
 *
 * Phase A — Selection:
 *   Spotlights the unselected PDF + image nodes via per-node halos.
 *   Shows a `N of 4 selected` counter.
 *   Advances to Phase B when all 4 nodes are selected.
 *
 * Phase B — Send:
 *   Pins `pinnedComposerPrompt` with the deck message so the chat
 *   composer hydrates with it. Spotlights the send button.
 *   On send, kicks off `seedOnboardingArtifacts` for the deck HTML and
 *   advances to Step 8.
 *
 * Target set: `data.pdfNodeId` (from Step 3) + `data.imageNodeIds` (from Step 6).
 */

import { useCanvasApi } from "@/pages/canvas/context/CanvasContext";
import {
  deckPosition,
  seedOnboardingArtifacts,
} from "@/pages/canvas/onboarding/seedOnboardingArtifacts";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useEffect, useMemo, useRef, type CSSProperties } from "react";
import { useCanvasFloatingChat } from "../../../../context/CanvasFloatingChatContext";
import {
  useCanvasOnboardingState,
  useCanvasOnboardingUpdate,
} from "../../../../context/CanvasOnboardingContext";
import { useCanvasRoute } from "../../../../context/CanvasRouteContext";
import { useCanvasSelectionMirror } from "../../../../context/CanvasSelectionMirrorContext";
import { useOnboardingAgentSession } from "../hooks/useOnboardingAgentSession";
import { MarqueeSelectDemo } from "../shared/MarqueeSelectDemo";
import { Flourish, StepHeader } from "../shared/StepHeader";
import type { StepProps } from "../shared/types";

const PINNED_ACTION_ID = "onboarding:gen-deck";

/** Pre-hosted AeroCarry deck HTML URL. */
const DECK_HTML_URL =
  "https://static.dev.cohesive.ai/images/onboarding/aerocarry_homepage.html";
const DECK_HTML_NAME = "AeroCarry Go-to-Market Deck.html";

const GEN_DECK_MESSAGE =
  "Can you make a go-to-market deck out of the product brief and these images on the canvas?";

export const GenerateDeckStep = memo(function GenerateDeckStep({
  id,
  data,
  onAdvance,
}: StepProps) {
  const update = useCanvasOnboardingUpdate();
  const { highlightEmphasisTick } = useCanvasOnboardingState();
  const { currentSessionId } = useCanvasFloatingChat();
  const { openOnboardingAgentSession } = useOnboardingAgentSession(id, data);

  // "Chat ready" means onboarding's *own* session is the active floating
  // chat — not merely "some chat is open". Mirrors GenerateImagesStep so
  // a pre-existing unrelated chat can't auto-skip to the send phase or
  // capture the scripted deck prompt.
  const onboardingSessionId = data.onboardingSessionId;
  const onboardingChatOpen =
    !!onboardingSessionId && currentSessionId === onboardingSessionId;

  // Bump the spotlight emphasis tick — used when the user clicks the
  // in-card "Click the highlighted prompt" row to nudge their eye back
  // to the send button's halo in case they missed the steady breathe.
  const emphasizeSendHighlight = () => {
    update({ highlightEmphasisTick: highlightEmphasisTick + 1 });
  };
  const { canvasId } = useCanvasRoute();
  const canvas = useCanvasApi();
  const queryClient = useQueryClient();

  const onAdvanceRef = useRef(onAdvance);
  onAdvanceRef.current = onAdvance;

  const seedTimerRef = useRef<number | null>(null);

  // Build the 4-node target set.
  const pdfNodeId = typeof data.pdfNodeId === "string" ? data.pdfNodeId : null;
  const imageNodeIds: string[] = Array.isArray(data.imageNodeIds)
    ? (data.imageNodeIds as string[])
    : [];

  const targetIds = useMemo(() => {
    const ids: string[] = [];
    if (pdfNodeId) ids.push(pdfNodeId);
    ids.push(...imageNodeIds);
    return ids;
  }, [pdfNodeId, imageNodeIds]);

  const targetIdSet = useMemo(() => new Set(targetIds), [targetIds]);
  const totalTarget = targetIds.length;

  const selectedNodes = useCanvasSelectionMirror();

  const selectedTargetCount = useMemo(
    () => selectedNodes.filter((n) => targetIdSet.has(n.id)).length,
    [selectedNodes, targetIdSet],
  );

  const allSelected = totalTarget > 0 && selectedTargetCount >= totalTarget;
  // If target set is empty (missing persisted data), skip to chat phase.
  const sealedEmpty = totalTarget === 0;
  const selectionDone = allSelected || sealedEmpty;

  // Once selection is done, (re)activate onboarding's dedicated chat
  // session — created back in Step 5 and persisted on the node, so this
  // re-opens the same isolated session (no duplicate) and self-heals if
  // the user switched the floating chat to another session in between.
  useEffect(() => {
    if (selectionDone) void openOnboardingAgentSession();
  }, [selectionDone, openOnboardingAgentSession]);

  // Phase management: update spotlight + pinned prompt.
  useEffect(() => {
    if (!selectionDone) {
      update({
        lockedViewport: false,
        highlightTargetId: null,
        pinnedComposerPrompt: null,
      });
    } else if (!onboardingChatOpen) {
      update({
        lockedViewport: false,
        highlightTargetId: "agent-chat-toggle",
        pinnedComposerPrompt: null,
      });
    } else {
      update({
        lockedViewport: false,
        highlightTargetId: "chat-send-button",
        pinnedComposerPrompt: {
          id: PINNED_ACTION_ID,
          message: GEN_DECK_MESSAGE,
        },
      });
    }
    return () => {
      update({
        highlightTargetId: null,
        pinnedComposerPrompt: null,
      });
    };
  }, [selectionDone, onboardingChatOpen, update]);

  // Detect send, start frontend deck seed, advance.
  useEffect(() => {
    const onSent = (e: Event) => {
      const detail = (e as CustomEvent<{ id?: string }>).detail;
      if (detail?.id !== PINNED_ACTION_ID) return;

      // Advance immediately so the UI moves to the next step.
      // Seed the deck after a short delay so it feels like the agent is
      // working before the node appears on the canvas.
      onAdvanceRef.current();

      if (canvasId) {
        // Seed unconditionally — advancing without seeding would strand
        // the next step waiting for a deck node that never lands. Fall
        // back to the canvas origin if the brief's drop point was never
        // captured.
        const dropPoint = (data.pdfDropPoint as
          | { x: number; y: number }
          | undefined) ?? {
          x: 0,
          y: 0,
        };
        const pos = deckPosition(dropPoint);
        seedTimerRef.current = window.setTimeout(() => {
          void seedOnboardingArtifacts(canvasId, queryClient, canvas, {
            kind: "html",
            url: DECK_HTML_URL,
            name: DECK_HTML_NAME,
            position: pos,
          });
        }, 5_000);
      }
    };

    window.addEventListener("cohesive:onboarding:composer-prompt-sent", onSent);
    return () => {
      window.removeEventListener(
        "cohesive:onboarding:composer-prompt-sent",
        onSent,
      );
      // Do NOT cancel seedTimerRef here — onAdvanceRef.current() causes this
      // component to unmount, which would fire this cleanup and cancel the
      // timer before it has a chance to run. seedOnboardingArtifacts only
      // captures canvasId/queryClient/canvas which remain valid after unmount.
    };
  }, [canvasId, data.pdfDropPoint, canvas, queryClient]);

  const phase: "select" | "chat" | "send" = !selectionDone
    ? "select"
    : !onboardingChatOpen
      ? "chat"
      : "send";

  return (
    <>
      {/* Looping marquee-drag demo encompassing ALL four targets
          (brief + three images) for the duration of the select phase.
          Anchored on the full target set rather than only the
          unselected ones so the demo's bounding box doesn't shrink as
          the user clicks individual nodes — teaching the marquee
          gesture as a single sweep over the whole group. */}
      {phase === "select" && targetIds.length > 0 && (
        <MarqueeSelectDemo nodeIds={targetIds} />
      )}

      <div style={{} as CSSProperties}>
        <StepHeader
          heading={
            <>
              Now turn it into a <Flourish>deck</Flourish>.
            </>
          }
          lede={
            phase === "select" ? (
              <>
                Select the brief and all three images, then send the next
                prompt. The agent will pull from every selected node to draft a
                full go-to-market presentation.
              </>
            ) : phase === "chat" ? (
              <>Your selection is ready — open the chat to send.</>
            ) : (
              <>
                We&apos;ve pre-filled the deck prompt. Hit send when you&apos;re
                ready.
              </>
            )
          }
        />
      </div>

      {/* Only the currently needed action is shown — the step swaps
          this single row as the user progresses (select → open chat →
          send), keeping the card short. */}
      <div
        className="_co_onb_settle_3"
        style={{
          marginTop: "var(--co-onb-stack-md)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--co-onb-stack-xs)",
        }}
      >
        {phase === "select" && (
          <SelectionRow
            label={
              totalTarget > 0
                ? `Select the brief and all three images · ${Math.min(
                    selectedTargetCount,
                    totalTarget,
                  )} of ${totalTarget} selected`
                : "Select the brief and all three images"
            }
          />
        )}
        {phase === "chat" && <SelectionRow label="Open the chat" />}
        {phase === "send" && (
          <SelectionRow
            label="Click the highlighted prompt"
            onClick={emphasizeSendHighlight}
          />
        )}
      </div>
    </>
  );
});

// ─── SelectionRow ─────────────────────────────────────────────────────────────

// Single active-step row. Styling mirrors `GenerateImagesStep`'s
// `StepRow` so the "open the chat" and "turn it into a deck" steps read
// at the same density.
const SelectionRow = memo(function SelectionRow({
  label,
  onClick,
}: {
  label: string;
  /** Optional — present on the send-phase row so the user can click
   *  to re-emphasize the spotlight on the chrome send button. */
  onClick?: () => void;
}) {
  const interactive = !!onClick;
  return (
    <div
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px",
        background: "var(--co-snap-fill)",
        border: "1px dashed var(--co-track-strong)",
        borderRadius: 12,
        transition: "opacity 320ms ease, background-color 200ms ease",
        textAlign: "left",
        cursor: interactive ? "pointer" : "default",
        userSelect: interactive ? "none" : undefined,
      }}
    >
      <span
        className="_co_onb_dot"
        style={{
          flexShrink: 0,
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: "var(--co-rust)",
          display: "inline-block",
        }}
      />
      <span
        style={{
          fontSize: 13,
          fontWeight: 500,
          letterSpacing: "-0.005em",
          color: "var(--co-fg)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {label}
      </span>
    </div>
  );
});
